import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Gift,
  CheckCircle,
  ExternalLink,
  Star,
  Zap,
  Clock,
  AlertCircle,
  XCircle,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface BetfredChallengeSignupProps {
  challengeId: number;
  title: string;
}

interface BetfredSignupData {
  hasSignedUp: boolean;
  signupDate?: string;
  verified: boolean;
  entriesAwarded: number;
  verificationStatus?: string;
  verificationAttempts?: number;
  betfredEmail?: string;
  id?: number;
}

export default function BetfredChallengeSignup({
  challengeId,
  title,
}: BetfredChallengeSignupProps) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Query user's Betfred signup status for this challenge
  const { data: signupData } = useQuery<BetfredSignupData>({
    queryKey: ["/api/betfred-challenge-signup", challengeId],
    enabled: isAuthenticated,
  });

  // Query global Betfred verification status (if user has ever been verified)
  const { data: globalStatus } = useQuery<{ hasBeenVerified: boolean }>({
    queryKey: ["/api/betfred-global-status"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
    staleTime: 0, // Always fresh data
    refetchOnWindowFocus: true,
  });

  // Signup mutation for challenges
  const signupMutation = useMutation({
    mutationFn: async ({
      challengeId,
      betfredEmail,
    }: {
      challengeId: number;
      betfredEmail: string;
    }) => {
      const payload = {
        challengeId,
        betfredEmail,
      };
      console.log("🟢 [BETFRED CHALLENGE] Sending request with payload:", payload);
      const response = await apiRequest("POST", "/api/betfred-signup", {
        body: payload
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/betfred-challenge-signup", challengeId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/betfred-global-status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/challenges", challengeId, "my-progress"],
      });

      toast({
        title: "Email Submitted Successfully!",
        description:
          "Verification will begin in 30 minutes. You'll receive 2 referrals once verified.",
        duration: 6000,
      });
      setEmail("");
    },
    onError: (error: any) => {
      console.error("Betfred signup error:", error);
      toast({
        title: "Signup Failed",
        description:
          error.message || "Failed to submit Betfred email. Please try again.",
        variant: "destructive",
        duration: 6000,
      });
    },
  });

  // Retry mutation for challenges
  const retryMutation = useMutation({
    mutationFn: async ({
      challengeId,
      betfredEmail,
    }: {
      challengeId: number;
      betfredEmail: string;
    }) => {
      const response = await apiRequest("POST", "/api/betfred-signup/retry", {
        body: {
          challengeId,
          betfredEmail,
        }
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/betfred-challenge-signup", challengeId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/challenges", challengeId, "my-progress"],
      });

      toast({
        title: "Retry Scheduled Successfully!",
        description: "Verification retry will begin in 30 minutes.",
        duration: 6000,
      });
      setEmail("");
    },
    onError: (error: any) => {
      console.error("Betfred retry error:", error);
      toast({
        title: "Retry Failed",
        description:
          error.message ||
          "Failed to retry Betfred verification. Please try again.",
        variant: "destructive",
        duration: 6000,
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your Betfred email address.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await signupMutation.mutateAsync({
        challengeId,
        betfredEmail: email.trim(),
      });
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your Betfred email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      await retryMutation.mutateAsync({
        challengeId,
        betfredEmail: email.trim(),
      });
    } catch (error) {
      console.error("Retry error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't show anything if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Only hide if user has actually been verified (not just if query is loading/undefined)
  if (globalStatus?.hasBeenVerified === true) {
    return null;
  }

  // If user has already signed up and is verified, show success message
  if (signupData?.hasSignedUp && signupData?.verified) {
    return (
      <Card className="border-2 border-green-200 bg-green-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg text-green-800">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Betfred Verification Complete!
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-gray-100 rounded-lg border">
            <div className="text-xs text-gray-500 mb-1">
              Your Betfred Email:
            </div>
            <div className="text-sm font-medium text-gray-800">
              {signupData.betfredEmail}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg border bg-green-100 border-green-200">
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                2 Referrals Awarded!
              </span>
            </div>
            <Badge variant="secondary" className="bg-green-200 text-green-800">
              +2 referrals awarded
            </Badge>
          </div>

          <div className="text-sm space-y-3">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span>Email verified with Betfred</span>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <Star className="h-4 w-4" />
              <span>2 bonus referrals added to your challenge progress</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If user has signed up but verification is pending/failed/invalid
  if (signupData?.hasSignedUp && !signupData?.verified) {
    const isPending = signupData.verificationStatus === "pending";
    const isFailed = signupData.verificationStatus === "failed";
    const isInvalid = signupData.verificationStatus === "invalid";
    const attempts = signupData.verificationAttempts || 0;
    const canRetry = isInvalid || isFailed;

    return (
      <Card
        className={`border-2 ${isPending ? "border-blue-200 bg-blue-50" : "border-orange-200 bg-orange-50"}`}
      >
        <CardHeader className="pb-4">
          <CardTitle
            className={`flex items-center gap-2 text-lg ${isPending ? "text-blue-800" : "text-orange-800"}`}
          >
            {isPending ? (
              <Clock className="h-5 w-5 text-blue-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-orange-600" />
            )}
            {isPending
              ? "Betfred Verification in Progress..."
              : "Betfred Verification Issue"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-gray-100 rounded-lg border">
            <div className="text-xs text-gray-500 mb-1">
              Your Betfred Email:
            </div>
            <div
              className={`text-sm font-medium ${isPending ? "text-gray-500" : "text-gray-800"}`}
            >
              {signupData.betfredEmail}
            </div>
          </div>

          <div
            className={`flex items-center justify-between p-3 rounded-lg border ${isPending ? "bg-blue-100 border-blue-200" : "bg-orange-100 border-orange-200"}`}
          >
            <div className="flex items-center gap-2">
              <Gift
                className={`h-4 w-4 ${isPending ? "text-blue-600" : "text-orange-600"}`}
              />
              <span
                className={`text-sm font-medium ${isPending ? "text-blue-700" : "text-orange-700"}`}
              >
                {isPending ? "2 Referrals Pending..." : "Verification Failed"}
              </span>
            </div>
            <Badge
              variant="secondary"
              className={`${isPending ? "bg-blue-200 text-blue-800" : "bg-orange-200 text-orange-800"}`}
            >
              {isPending ? "+2 referrals pending" : "No referrals awarded"}
            </Badge>
          </div>

          <div className="text-sm space-y-3">
            {isPending && (
              <>
                <div className="flex items-center gap-2 text-blue-600">
                  <Clock className="h-4 w-4" />
                  <span>Verification scheduled - please wait</span>
                </div>
                <div className="flex items-center gap-2 text-blue-600">
                  <Zap className="h-4 w-4" />
                  <span>You'll receive 2 bonus referrals once verified</span>
                </div>
              </>
            )}

            {(isFailed || isInvalid) && (
              <>
                <div className="flex items-center gap-2 text-orange-600">
                  <XCircle className="h-4 w-4" />
                  <span>
                    {isInvalid
                      ? "Email not found in Betfred's system"
                      : "Verification failed due to technical issue"}
                  </span>
                </div>
                {isInvalid && (
                  <div className="text-xs text-orange-700 bg-orange-100 p-2 rounded">
                    Make sure you're registered with Betfred and using the same
                    email address.
                  </div>
                )}
                
                {/* Retry Section */}
                {canRetry && (
                  <div className="pt-3 border-t space-y-4">
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 mb-2">Retry Verification</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Enter your Betfred email to retry verification
                      </p>
                    </div>

                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-sm font-medium text-blue-900 mb-2">Previous email: {signupData.betfredEmail}</div>
                      <form onSubmit={handleRetry} className="space-y-3">
                        <Input
                          type="email"
                          placeholder="Enter your Betfred email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isSubmitting}
                          className="w-full"
                          data-testid="input-retry-email"
                        />
                        <Button
                          type="submit"
                          disabled={isSubmitting || !email.trim()}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="button-retry-verification"
                        >
                          {isSubmitting ? (
                            <>
                              <Clock className="h-4 w-4 mr-2 animate-spin" />
                              Scheduling Retry...
                            </>
                          ) : (
                            <>
                              <AlertCircle className="h-4 w-4 mr-2" />
                              Retry Verification
                            </>
                          )}
                        </Button>
                      </form>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // If user has never signed up, show the signup form
  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 hidden">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg text-blue-800">
          <Gift className="h-5 w-5 text-blue-600" />
          Betfred Challenge Bonus
        </CardTitle>
        <div className="text-sm text-blue-700">
          Get 2 bonus referrals by verifying your Betfred account!
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Bonus Highlight */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <Star className="h-6 w-6" />
            <div>
              <div className="font-bold text-lg">+2 Challenge Referrals</div>
              <div className="text-sm text-blue-100">
                Boost your leaderboard position
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="space-y-2 text-sm text-gray-700">
          <div className="font-medium text-gray-900">How it works:</div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Enter your Betfred account email below</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>We'll verify your account with Betfred</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>
                Get 2 bonus referrals added to your challenge progress
              </span>
            </div>
          </div>
        </div>

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="betfred-email"
              className="text-sm font-medium text-gray-700"
            >
              Betfred Email Address
            </label>
            <Input
              id="betfred-email"
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full"
              data-testid="input-betfred-email"
            />
            <div className="text-xs text-gray-500">
              Use the same email address you registered with Betfred
            </div>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting || signupMutation.isPending}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
            data-testid="button-submit-betfred-email"
          >
            {isSubmitting || signupMutation.isPending ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Gift className="h-4 w-4 mr-2" />
                Get My 2 Bonus Referrals
              </>
            )}
          </Button>
        </form>

        {/* Learn More Link */}
        <div className="text-center pt-2">
          <a
            href="https://betfredsa.click/o/3F2EJM"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1"
            data-testid="link-betfred-website"
          >
            <span>New to Betfred? Sign up here</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
